import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import MultiSelectGrade from './MultiSelectGrade';
import styles from './MultiSelectGradeList.module.less';
import clsx from 'clsx';
import AccountContext from 'website/services/AccountContext';

// todos disable when a button is clicked
// todos Mobile multi select chips
// eslint-disable-next-line no-unused-vars
const MultiSelectGradeList = ({
  filterObject = [],
  onClick,
  // contentRequestInProcess,
}) => {
  // Get member id info
  const { memberId } = useContext(AccountContext);

  // EDENG-3939: These are only supported specific grade range
  const schoolLevels = [
    {
      urlSlice: 'early-childhood',
      name: 'Early Childhood',
      count: 0,
      startGradeIds: 0, // Pre-K
      endGradeIds: 1, // K
    },
    {
      urlSlice: 'elementary-school',
      name: 'Elementary School',
      count: 0,
      startGradeIds: 1, // K
      endGradeIds: 6, // 5th
    },
    {
      urlSlice: 'middle-school',
      name: 'Middle School',
      count: 0,
      startGradeIds: 7, // 6th
      endGradeIds: 9, // 8th
    },
  ];

  // get all grades and sort these grades based on their id
  const allGradesTemp = filterObject.attributes.sort((grade1, grade2) => {
    return grade1.id - grade2.id;
  });

  // For some reason, trying to perform href generation causes issues. Use structured clone to make a deep copy
  const allGrades = JSON.parse(JSON.stringify(allGradesTemp));
  // Structured clone does not exist in our ESLint version (or node16), will need to upgrade at some point. Use alternative for now
  // const allGrades = structuredClone(allGradesTemp);

  const gradeUrlSlicePrefix = 'grade-';

  // Check whether we have a range selected
  const numberOfSelectedGrades = allGrades.filter(
    grade => grade.selected === true
  ).length;

  // We look for the first and last selected items to apply associated UI with these grade filters
  let rangeStart = undefined;
  let rangeEnd = undefined;

  if (numberOfSelectedGrades === 0) {
    // No grades selected, don't need to check
  } else if (numberOfSelectedGrades === 1) {
    // One grade selected, only need to check one.
    // Note for the range start we check if it is selected AND the content count > 0; this is because the filters can
    // be applied in any order and a previously valid grade range may not work properly later
    rangeStart = allGrades.find(
      element => element.selected === true && element.count > 0
    );
    rangeEnd = rangeStart;
  } else {
    rangeStart = allGrades.find(
      element => element.selected === true && element.count > 0
    );
    // Array.findLast does not exist in our ESLint version (or node16), will need to upgrade at some point. Use alternative for now
    // rangeEnd = allGrades.findLast(
    rangeEnd = [...allGrades]
      .reverse()
      .find(element => element.selected === true && element.count > 0);
  }

  // Determine whether a grade is/could in selected state by checking the two conditions
  //  1. The grade id is in between the rangeStart and rangeEnd where rangeStart and rangeEnd are not null
  //  2. The content matched in this grade is greater than 0
  const isSelected = grade => {
    if (!memberId) {
      // for anonymous users, it only supports single selection, so if the grade is selected, the grade id must equal to
      // the rangeStart and the rangeEnd where rangeStart and rangeEnd are not null
      return rangeStart !== undefined &&
        grade.id === rangeEnd.id &&
        grade.id === rangeStart.id &&
        grade.count >= 1;
    }

    return rangeStart !== undefined &&
      grade.id >= rangeStart.id &&
      grade.id <= rangeEnd.id &&
      grade.count >= 1;
  };

  // if both ends of selected grade range are same as the both ends of input school level,
  // then return false; Otherwise, return true
  const isSelectedSchoolLevel = schoolLevel =>
    rangeStart !== undefined &&
    schoolLevel.startGradeIds === rangeStart.id &&
    schoolLevel.endGradeIds === rangeEnd.id;

  // Generate new href for each grade
  // Not pretty, but call a foreach, and when you generate the new href replace the existing value;
  allGrades.forEach(function(grade, index, allGrades) {
    const baseGradeString = gradeUrlSlicePrefix + grade.urlSlice;
    let newGradeString = '';

    if (numberOfSelectedGrades === 0) {
      // If no grade selected, can use provided hrefs. No action needed
    } else if (!memberId) {
      if (grade.id === rangeStart.id && grade.id === rangeEnd.id) {
        // it is the selected grade, and we are removing selected range
        // while log-out users only can single select for grade level
        newGradeString = '';
        allGrades[index].href = grade.href.replace(
          baseGradeString,
          newGradeString
        );
      }
    } else if (numberOfSelectedGrades === 1) {
      // If one grade selected, we need to generate a range based on whether it is above or below
      if (grade.id > rangeStart.id) {
        // If given grade is larger than selected grade, it is the end of the range
        newGradeString =
          gradeUrlSlicePrefix + rangeStart.urlSlice + '-' + grade.urlSlice;
      } else if (grade.id < rangeStart.id) {
        // Else, it is the start of the range
        newGradeString =
          gradeUrlSlicePrefix + grade.urlSlice + '-' + rangeStart.urlSlice;
      } else {
        // Otherwise, it is the selected grade, we are removing the grade
        newGradeString = '';
      }
      allGrades[index].href = grade.href.replace(
        baseGradeString,
        newGradeString
      );
    } else {
      // Range already selected need to find out which is selected, and determine which href to generate
      if (grade.id < rangeStart.id) {
        // If given grade is less than start range, it is the new start
        newGradeString =
          gradeUrlSlicePrefix + grade.urlSlice + '-' + rangeEnd.urlSlice;
      } else if (
        grade.id > rangeEnd.id ||
        (grade.id > rangeStart.id && grade.id < rangeEnd.id)
      ) {
        // If given grade is greater than start range or is in between start and end range it is the new end
        newGradeString =
          gradeUrlSlicePrefix + rangeStart.urlSlice + '-' + grade.urlSlice;
      } else {
        // Otherwise, check the grade chip is for the start/end of the range
        // If it does, we are removing this grade; Revert back to a single grade select of the remaining range marker
        if (grade.id === rangeStart.id) {
          newGradeString = gradeUrlSlicePrefix + rangeEnd.urlSlice;
        } else if (grade.id === rangeEnd.id) {
          newGradeString = gradeUrlSlicePrefix + rangeStart.urlSlice;
        }
      }
      allGrades[index].href = grade.href.replace(
        baseGradeString,
        newGradeString
      );
    }

    // Replace double slashes if they exist (usually from blank replacement)
    if (newGradeString === '') {
      allGrades[index].href = grade.href.replace('//', '/');
    }
  });

  schoolLevels.forEach(function(schoolLevel, index, schoolLevels) {
    // Set the count of the school level to a non-zero value if there are contents for both end point grades
    const countForStartGrade = allGrades[schoolLevel.startGradeIds].count;
    const countForEndGrade = allGrades[schoolLevel.endGradeIds].count;
    if (countForStartGrade > 0 && countForEndGrade > 0) {
      schoolLevels[index].count += countForStartGrade + countForEndGrade;
    }

    if (isSelectedSchoolLevel(schoolLevel)) {
      // removing selected range
      schoolLevels[index].href = filterObject.href
        .replace(gradeUrlSlicePrefix, '')
        .replace('//', '/');
    } else {
      schoolLevels[index].href = filterObject.href.replace(
        gradeUrlSlicePrefix,
        schoolLevel.urlSlice
      );
    }
  });

  return (
    <>
      <ul className={clsx(styles.facets, styles.topLevel)} data-cy="grade-list">
        {allGrades.map(grade => (
          <MultiSelectGrade
            key={grade.urlSlice}
            displayName={grade.shortName}
            internalName={grade.internalName}
            urlName={grade.urlName}
            href={grade.href}
            disabled={grade.count < 1}
            selected={isSelected(grade)}
            onClick={onClick}
          />
        ))}
      </ul>
      <ul
        className={clsx(styles.facets, styles.isSchoolLevel, styles.topLevel)}
        data-cy="grade-range-list"
      >
        {schoolLevels.map(schoolLevel => (
          <MultiSelectGrade
            key={schoolLevel.urlSlice}
            displayName={schoolLevel.name}
            internalName={schoolLevel.urlSlice}
            urlName={schoolLevel.urlSlice}
            href={schoolLevel.href}
            disabled={schoolLevel.count < 1}
            selected={isSelectedSchoolLevel(schoolLevel)}
            onClick={onClick}
          />
        ))}
      </ul>
    </>
  );
};

export default MultiSelectGradeList;

MultiSelectGradeList.propTypes = {
  filterObject: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  contentRequestInProcess: PropTypes.bool.isRequired,
};
